import React, { useMemo } from 'react';
// import { CheckOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useAuth } from '../../../providers/AuthProvider';

const COLORS = {
    finish: TB.config('color.success'),
    process: '#7C3AED',
    wait: '#D4D0E0',
    waitText: '#A8A3B8',
};

const useStepperState = (auth) => {
    return useMemo(() => {
        let steps = [
            { title: 'Create account', status: 'finish' },
            { title: 'Complete application', status: 'process', description: 'In Progress' },
            { title: 'Build Profile', status: 'wait' },
            { title: 'Live', status: 'wait' },
        ];

        if (!auth) {
            steps[0] = { ...steps[0], status: 'process' };
            steps[1] = { ...steps[1], status: 'wait', description: null };
            return steps;
        }

        const isApproved = auth.isApplicationApproved?.();
        const isBeingReviewed = auth.isBeingReviewed?.();
        const hasCompletedProfile = auth.hasCompletedProfile?.();
        const isProfileApproved = auth.isProfileApproved?.();
        const isPendingProfile = auth.isPendingProfileApproved?.();
        const isProfileReady = auth.isProfileReadyForApproval?.();


        if (isBeingReviewed) {
            steps[1].description = 'Under Review';
        }

        if (auth.isApplicationRejected?.()) {
            steps[1].description = 'Not Approved';
        }

        if (isApproved) {
            steps[1] = { ...steps[1], status: 'finish', description: '' };
            steps[2] = { ...steps[2], status: 'process', description: 'In Progress' };

            if (hasCompletedProfile) steps[2].description = 'Complete';
            // "warn" tone renders the current step in amber (needs the user's action)
            if (isProfileReady) steps[2] = { ...steps[2], description: 'Ready to Submit', tone: 'warn' };
            // if (isPendingProfile) steps[2].description = 'Under Review';

            if (isPendingProfile) {
                // steps[2] = { ...steps[2], status: 'finish', description: '' };
                steps[2] = { ...steps[2], description: 'Under Review' };
                // steps[3] = { ...steps[3], status: 'process', description: 'Under Review' };
            }

            // A pending appeal is under review, even though the profile still carries its rejection
            if (auth.isProfilePendingAppeal?.()) {
                steps[2] = { ...steps[2], description: 'Under Review' };
            } else if (auth.isProfileRejected?.() || auth.isProfileAppealRejected?.()) {
                steps[2] = { ...steps[2], description: 'Changes Requested', tone: 'warn' };
            }

            if (isProfileApproved) {
                steps[2] = { ...steps[2], status: 'finish', description: '' };
                steps[3] = { ...steps[3], status: 'process', description: 'Active' };
            }
        }

        return steps;
    }, [auth]);
};


const StepCircle = ({ index, status, tone }) => {
    const isFinish = status === 'finish';
    const isProcess = status === 'process';
    const isWarn = tone === 'warn';

    const bg = isFinish ? 'bg-success' : isProcess ? (isWarn ? 'bg-amber-500' : 'bg-secondary') : 'bg-transparent';
    const border = isFinish ? 'border-success' : isProcess ? (isWarn ? 'border-amber-500' : 'border-secondary') : 'border-gray-300';
    const color = (isFinish || isProcess) ? 'text-white' : 'text-gray-400';
    const fontSize = isFinish ? 'text-[14px]' : 'text-[13px]';

    return (
        <div className={`flex items-center justify-center rounded-full font-semibold flex-shrink-0 h-[32px] w-[32px] border-2 ${bg} ${border} ${color} ${fontSize}`}>
            {isFinish ? <Check className="h-5 w-5" /> : index + 1}
            {/* {isFinish ? <CheckOutlined /> : index + 1} */}
        </div>
    );
};

const StepLabel = ({ step }) => (
    <div className="flex flex-col sm:items-center">
        <span className={`text-[11px] whitespace-nowrap font-medium ${step.status === 'wait' ? 'text-gray-400' : ''}`}>
            {step.title}
        </span>
        {step.description && (
            <span className={`text-[10px] whitespace-nowrap rounded-full px-3 py-0.5 text-white ${step.tone === 'warn' ? 'bg-amber-500' : 'bg-secondary'}`}>
                {step.description}
            </span>
        )}
    </div>
);

const connectorColor = (status) => status === 'finish' ? COLORS.finish : COLORS.wait;

const ApplicationStepper = ({ vertical = false }) => {
    const { auth } = useAuth();
    const steps = useStepperState(auth);
    const mobileClasses = vertical ? 'block' : 'block md:hidden'
    const desktopClasses = vertical ? 'hidden' : 'hidden md:flex max-w-2xl mx-auto';

    return (
        <div>
            <ol className={`${mobileClasses} m-0 p-0 list-none`}>
                {steps.map((step, i) => {
                    const done = step.status === 'finish';
                    const current = step.status === 'process';
                    const upcoming = step.status === 'wait';
                    const last = i === steps.length - 1;

                    const Comp = current ? Link : 'div';
                    const componentProps = current
                        ? { to: '/talent/application', 'aria-current': 'step' }
                        : { 'aria-disabled': true, role: 'link' }; // Good for accessibility

                    return (
                        <li key={step.title} className="flex gap-3">
                            {/* Indicator column: dot + connector that stretches to the next row */}
                            <div className="flex flex-col items-center">
                                <span
                                    className={TB.mapClass('flex h-5 w-5 shrink-0 items-center justify-center rounded-full', {
                                        'bg-success text-white': done,
                                        'bg-secondary text-white ring-4 ring-secondary/15': current && step.tone !== 'warn',
                                        'bg-amber-500 text-white ring-4 ring-amber-500/15': current && step.tone === 'warn',
                                        'bg-gray-100 text-gray-500 border border-gray-300': upcoming,
                                    })}
                                >
                                    {done && <Check className="h-3 w-3" strokeWidth={3} />}
                                    {current && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                                    {upcoming && (
                                        <span className="block text-[10px] font-semibold leading-none tabular-nums">
                                            {i + 1}
                                        </span>
                                    )}
                                </span>
                                {!last && (
                                    <span
                                        aria-hidden="true"
                                        className={TB.mapClass('my-1 w-px flex-1 min-h-[10px]', {
                                            'bg-success': done,
                                            'bg-gray-200': !done,
                                        })}
                                    />
                                )}
                            </div>

                            <Comp
                                {...componentProps}
                                className={TB.mapClass('flex flex-col min-w-0 no-underline hover:no-underline', {
                                    'pb-3': !last,
                                })}
                            >
                                <span
                                    className={TB.mapClass('text-sm leading-5 truncate', {
                                        'text-gray-600': done,
                                        'font-semibold text-gray-900 hover:text-gray-900': current,
                                        'text-gray-400': upcoming,
                                    })}
                                >
                                    {step.title}
                                </span>
                                {current && step.description && (
                                    <span className={`mt-0.5 text-[10px] font-semibold uppercase tracking-wider leading-3 ${step.tone === 'warn' ? 'text-amber-600' : 'text-secondary'}`}>
                                        {step.description}
                                    </span>
                                )}
                            </Comp>
                        </li>
                    );
                })}
            </ol>


            <div className={desktopClasses}>
                {steps.map((step, i) => {
                    const Comp = step.status === 'process' ? Link : 'div';
                    const componentProps = step.status === 'process' ? { to: '/talent/application' } : { 'aria-disabled': true, role: 'link' }; // Good for accessibility
                    const isLast = i === steps.length - 1;

                    return (
                        <Comp
                            key={i}
                            className="flex flex-col items-center relative flex-1 !min-w-[unset]"
                            {...componentProps}
                        >
                            {!isLast && (
                                <div
                                    className="absolute h-0.5"
                                    style={{
                                        left: 'calc(50% + 22px)',
                                        right: 'calc(-50% + 22px)',
                                        top: 15,
                                        backgroundColor: connectorColor(step.status),
                                    }}
                                />
                            )}
                            <StepCircle index={i} status={step.status} tone={step.tone} />
                            <div className="mt-1.5">
                                <StepLabel step={step} />
                            </div>
                        </Comp>
                    )
                })}
            </div>
        </div>
    );
}

export default ApplicationStepper;